import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Badge } from '../components/ui';
import { getLocalSendingIds, MASSENMAIL_SENDING_EVENT } from '../lib/massenmailSendFeedback';
import RecipientOverview from '../components/RecipientOverview';
import ElternbriefShareModal from '../components/ElternbriefShareModal';
import {
  AppointmentBooking,
  ExportModule,
  buildElternbriefResultsCsv,
  downloadCsv
} from '../utils/elternbriefExport';
import { 
  PlusIcon, 
  EyeIcon, 
  DocumentDuplicateIcon, 
  TrashIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  PencilIcon,
  UserGroupIcon,
  UserPlusIcon,
  EllipsisVerticalIcon,
  ArrowUturnLeftIcon,
  PaperClipIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface Elternbrief {
  id: number;
  titel: string;
  inhalt: string;
  status: 'entwurf' | 'versendet' | 'archiviert';
  sending?: boolean;
  erstellt_am: string;
  versand_datum: string | null;
  anzahl_empfaenger: number;
  anzahl_antworten: number;
  anzahl_geoeffnet: number;
  aktualisiert_am?: string;
  shared?: boolean;
  shared_permission?: 'read' | 'write';
  shared_by?: {
    first_name?: string;
    last_name?: string;
    username?: string;
  };
  statistik: {
    total: number;
    geoeffnet: number;
    beantwortet: number;
    gesperrt: number;
    abgelaufen: number;
    ausstehend: number;
    ruecklaufquote: number;
  };
}

interface MassenmailProps {
  embedded?: boolean;
  onRefresh?: () => void;
}

interface UmfrageStatistik {
  frage_id: number;
  frage_text: string;
  frage_typ: string;
  antworten: Record<string, { count: number; percentage: number; text?: string }>;
  freitext_antworten?: string[];
  total_antworten: number;
}

interface ModulStatistik {
  modul_id: number;
  modul_titel: string;
  modul_typ: string;
  total_antworten: number;
  umfrage_statistik?: UmfrageStatistik[];
}

interface DetailModule {
  id: number;
  modul_typ: string;
  titel?: string | null;
  konfiguration?: { appointment_config_id?: number };
  anhaenge?: Array<{ id: number; original_dateiname?: string; dateiname: string; dateigroesse?: number }>;
  appointment_config?: { id: number; title: string; public_token: string };
  fragen?: Array<{ id: number }>;
}

interface FullElternbrief extends Elternbrief {
  module?: DetailModule[];
}

interface ElternbriefDetailViewProps {
  brief: Elternbrief | null;
  onClose: () => void;
  onShowRecipients: (b: Elternbrief) => void;
  onNavigate: (path: string) => void;
  onExport: (id: number) => void;
  onDuplicate: (id: number) => void;
  onShare: (id: number) => void;
  onDelete: (id: number) => void;
  onRestore: (id: number) => void;
  onPermanentDelete: (id: number) => void;
  filter: string;
  isShared: (b: Elternbrief) => boolean;
  /** Im eingebetteten Modus (z. B. E-Mail) ist „Ansehen“ redundant – Detail ist bereits sichtbar */
  embedded?: boolean;
}

interface RecipientWithStatus {
  id: number;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
  empfaenger_name?: string;
  empfaenger_email?: string;
  geoeffnet_am?: string | null;
  beantwortet_am?: string | null;
  token_gueltig_bis?: string;
  antwort_status?: 'beantwortet' | 'geoeffnet' | 'gesperrt' | 'abgelaufen' | 'ausstehend';
}

interface ExportRecipientWithAntworten {
  id: number;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
  empfaenger_name?: string;
  empfaenger_email?: string;
  antworten?: Array<{
    modul_typ: string;
    antwort_data?: { umfrage_antworten?: Array<{ frage_id: number; ausgewaehlte_optionen?: string[] }> };
  }>;
}

function HoverNamesTooltip({
  names,
  children,
  className = '',
  onClick
}: {
  names: string[];
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {children}
      {show && names.length > 0 && (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg max-w-xs max-h-48 overflow-y-auto whitespace-normal pointer-events-none">
          <div>{names.join(', ')}</div>
          {onClick && <div className="mt-1 pt-1 border-t border-gray-600 text-gray-400">Klicken für Details</div>}
        </div>
      )}
    </div>
  );
}

function DetailModal({
  isOpen,
  onClose,
  title,
  items
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: Array<{ name: string; subtext?: string; email?: string; extra?: string }>;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={onClose} role="dialog" aria-modal aria-labelledby="detail-modal-title">
      <div
        className="card shadow-xl max-w-md w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="detail-modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Keine Einträge</p>
          ) : (
            <ul className="space-y-3">
              {items.map((item, i) => (
                <li key={i} className="flex flex-col gap-0.5 py-2 border-b border-gray-100 dark:border-gray-800 dark:border-gray-800 last:border-0">
                  <span className="font-medium text-gray-900 dark:text-gray-50">{item.name}</span>
                  {item.subtext && <span className="text-sm text-gray-600 dark:text-gray-400">{item.subtext}</span>}
                  {item.email && (
                    <a href={`mailto:${item.email}`} className="text-sm text-blue-600 hover:underline">
                      {item.email}
                    </a>
                  )}
                  {item.extra && <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.extra}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const ElternbriefDetailView: React.FC<ElternbriefDetailViewProps> = ({
  brief,
  onClose,
  onShowRecipients,
  onNavigate,
  onExport,
  onDuplicate,
  onShare,
  onDelete,
  onRestore,
  onPermanentDelete,
  filter,
  isShared,
  embedded = false
}) => {
  const [responseStats, setResponseStats] = useState<ModulStatistik[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [recipients, setRecipients] = useState<RecipientWithStatus[]>([]);
  const [exportResults, setExportResults] = useState<ExportRecipientWithAntworten[]>([]);
  const [detailModal, setDetailModal] = useState<{ title: string; items: Array<{ name: string; subtext?: string; email?: string; extra?: string }> } | null>(null);
  const [fullBrief, setFullBrief] = useState<FullElternbrief | null>(null);
  const [appointmentBookings, setAppointmentBookings] = useState<AppointmentBooking[]>([]);
  const [appointmentConfigs, setAppointmentConfigs] = useState<Array<{ id: number; title: string; public_token: string }>>([]);
  const umfrageSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (brief?.id) {
      api.get(`/massenmail/${brief.id}`).then((res) => {
        if (res.data?.success && res.data?.data) {
          setFullBrief(res.data.data);
        }
      }).catch(() => setFullBrief(null));
    } else {
      setFullBrief(null);
      setAppointmentBookings([]);
    }
  }, [brief?.id]);

  useEffect(() => {
    api.get('/appointments/configs').then((r) => {
      if (r.data?.success && Array.isArray(r.data.data)) {
        setAppointmentConfigs(r.data.data.map((c: any) => ({ id: c.id, title: c.title, public_token: c.public_token })));
      }
    }).catch(() => setAppointmentConfigs([]));
  }, []);

  useEffect(() => {
    if (!fullBrief?.module) {
      setAppointmentBookings([]);
      return;
    }
    const terminModules = fullBrief.module.filter(
      (m) => m.modul_typ === 'terminbuchen' && m.konfiguration?.appointment_config_id
    );
    if (terminModules.length === 0) return;
    Promise.all(
      terminModules.map((mod) =>
        api.get(`/appointments/configs/${mod.konfiguration!.appointment_config_id}/slots`).then((r) => {
          const slots = r.data?.data || [];
          return slots
            .filter((s: any) => s.booking_id && s.booking_status && s.booking_status !== 'cancelled')
            .map((s: any) => ({
              configId: mod.konfiguration!.appointment_config_id,
              moduleTitle: mod.titel || 'Terminbuchung',
              start_time: s.start_time,
              end_time: s.end_time,
              parent_name: s.parent_name,
              parent_email: s.parent_email,
              student_name: s.student_name,
              booking_status: s.booking_status,
              notes: s.notes
            }));
        }).catch(() => [])
      )
    ).then((arr) => setAppointmentBookings(arr.flat()));
  }, [fullBrief?.module]);

  useEffect(() => {
    if (brief?.id && brief.status === 'versendet') {
      setLoadingStats(true);
      api.get(`/massenmail/${brief.id}/response-statistics`)
        .then((res) => {
          if (res.data?.success && Array.isArray(res.data.data)) {
            setResponseStats(res.data.data);
          }
        })
        .catch(() => setResponseStats([]))
        .finally(() => setLoadingStats(false));
    } else {
      setResponseStats([]);
    }
  }, [brief?.id, brief?.status]);

  useEffect(() => {
    if (brief?.id && brief.status === 'versendet') {
      Promise.all([
        api.get(`/massenmail/${brief.id}/recipients`),
        api.get(`/massenmail/${brief.id}/export-results`)
      ])
        .then(([recipRes, exportRes]) => {
          if (recipRes.data?.success && Array.isArray(recipRes.data.data)) {
            setRecipients(recipRes.data.data);
          }
          if (exportRes.data?.success && Array.isArray(exportRes.data.data)) {
            setExportResults(exportRes.data.data);
          }
        })
        .catch(() => {
          setRecipients([]);
          setExportResults([]);
        });
    } else {
      setRecipients([]);
      setExportResults([]);
    }
  }, [brief?.id, brief?.status]);

  if (!brief) {
    return (
      <div className="card h-full flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
        <PaperAirplaneIcon className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-lg font-medium">Elternbrief auswählen</p>
        <p className="text-sm mt-1">Wählen Sie in der Liste links einen Elternbrief aus.</p>
      </div>
    );
  }

  const stat = brief.statistik;
  const umfrageModuleStats = responseStats.filter((m) => m.modul_typ === 'umfrage');

  const formatName = (r: RecipientWithStatus | ExportRecipientWithAntworten) =>
    `${r.Vorname || ''} ${r.Name || ''}`.trim() || r.empfaenger_name || `ID ${r.id}`;

  const allNames = recipients.map(formatName);
  const geoeffnetNames = recipients.filter((r) => r.antwort_status === 'geoeffnet' || r.antwort_status === 'beantwortet').map(formatName);
  const beantwortetNames = recipients.filter((r) => r.antwort_status === 'beantwortet').map(formatName);
  const ausstehendNames = recipients.filter((r) => r.antwort_status === 'ausstehend').map(formatName);
  const gesperrtNames = recipients.filter((r) => r.antwort_status === 'gesperrt').map(formatName);
  const abgelaufenNames = recipients.filter((r) => r.antwort_status === 'abgelaufen').map(formatName);

  const surveyNamesByOption: Record<number, Record<string, string[]>> = {};
  const surveyRecipientsByOption: Record<number, Record<string, ExportRecipientWithAntworten[]>> = {};
  exportResults.forEach((r) => {
    (r.antworten || []).forEach((a) => {
      if (a.modul_typ === 'umfrage' && a.antwort_data?.umfrage_antworten) {
        const name = formatName(r);
        a.antwort_data.umfrage_antworten.forEach((entry: { frage_id: number; ausgewaehlte_optionen?: (string | number)[] }) => {
          const ids = entry.ausgewaehlte_optionen || [];
          ids.forEach((opt) => {
            const optId = String(opt);
            if (!surveyNamesByOption[entry.frage_id]) surveyNamesByOption[entry.frage_id] = {};
            if (!surveyNamesByOption[entry.frage_id][optId]) surveyNamesByOption[entry.frage_id][optId] = [];
            surveyNamesByOption[entry.frage_id][optId].push(name);
            if (!surveyRecipientsByOption[entry.frage_id]) surveyRecipientsByOption[entry.frage_id] = {};
            if (!surveyRecipientsByOption[entry.frage_id][optId]) surveyRecipientsByOption[entry.frage_id][optId] = [];
            surveyRecipientsByOption[entry.frage_id][optId].push(r);
          });
        });
      }
    });
  });

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleString('de-DE') : '');

  const openRecipientDetailModal = (
    title: string,
    list: RecipientWithStatus[] | ExportRecipientWithAntworten[]
  ) => {
    const items = list.map((r) => {
      const rec = r as RecipientWithStatus;
      const extra =
        rec.geoeffnet_am
          ? `Geöffnet: ${formatDate(rec.geoeffnet_am)}${rec.beantwortet_am ? ` · Beantwortet: ${formatDate(rec.beantwortet_am)}` : ''}`
          : undefined;
      return {
        name: formatName(r),
        subtext: r.Klasse ? `Klasse ${r.Klasse}` : undefined,
        email: (r as RecipientWithStatus).empfaenger_email ?? (r as ExportRecipientWithAntworten).empfaenger_email,
        extra
      };
    });
    setDetailModal({ title, items });
  };

  const openSurveyDetailModal = (title: string, list: ExportRecipientWithAntworten[]) => {
    const items = list.map((r) => ({
      name: formatName(r),
      subtext: r.Klasse ? `Klasse ${r.Klasse}` : undefined,
      email: r.empfaenger_email
    }));
    setDetailModal({ title, items });
  };

  return (
    <div className="card h-full flex flex-col overflow-hidden">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-50">{brief.titel || '(Ohne Titel)'}</h2>
            {getStatusBadge(brief.status, brief.sending)}
            {brief.shared && (
              <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700">Geteilt</span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Erstellt: {new Date(brief.erstellt_am).toLocaleDateString('de-DE')}
            {brief.versand_datum && ` · Versendet: ${new Date(brief.versand_datum).toLocaleDateString('de-DE')}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg"
            title="Schließen"
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          {!embedded && (
            <button
              onClick={() => onNavigate(`/elternbriefe/${brief.id}`)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Ansehen"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
          )}
          {brief.status === 'versendet' && (
            <button
              onClick={() => onShowRecipients(brief)}
              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
              title="Empfänger"
            >
              <UserGroupIcon className="h-5 w-5" />
            </button>
          )}
          {!isShared(brief) && brief.status === 'entwurf' && !brief.sending && filter !== 'papierkorb' && (
            <button
              onClick={() => onNavigate(`/elternbriefe/${brief.id}/bearbeiten`)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg"
              title="Bearbeiten"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}
          {!isShared(brief) && (
            <button onClick={() => onDuplicate(brief.id)} className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg" title="Duplizieren">
              <DocumentDuplicateIcon className="h-5 w-5" />
            </button>
          )}
          {brief.status === 'versendet' && (
            <button onClick={() => onExport(brief.id)} className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg" title="Exportieren">
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
          )}
          {!isShared(brief) && filter !== 'papierkorb' && (
            <button onClick={() => onShare(brief.id)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Teilen">
              <UserPlusIcon className="h-5 w-5" />
            </button>
          )}
          {!isShared(brief) && filter !== 'papierkorb' && (
            <button onClick={() => onDelete(brief.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="In Papierkorb">
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
          {!isShared(brief) && filter === 'papierkorb' && (
            <>
              <button onClick={() => onRestore(brief.id)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Wiederherstellen">
                <ArrowUturnLeftIcon className="h-5 w-5" />
              </button>
              <button onClick={() => onPermanentDelete(brief.id)} className="p-2 text-red-700 hover:bg-red-50 rounded-lg" title="Endgültig löschen">
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Inhalt */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">Inhalt</h3>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 dark:text-gray-200 text-sm">{brief.inhalt || '(Kein Inhalt)'}</div>
          </div>
        </section>

        {/* Module im Überblick: Anhänge, Termine, sonstiges */}
        {fullBrief?.module && fullBrief.module.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">Module im Überblick</h3>
            <div className="space-y-3">
              {fullBrief.module.map((mod) => {
                if (mod.modul_typ === 'dateianhang') {
                  const formatSize = (bytes?: number) =>
                    bytes == null ? '' : bytes < 1024 ? bytes + ' B' : (bytes / 1024).toFixed(1) + ' KB';
                  const anhaenge = mod.anhaenge || [];
                  return (
                    <div key={mod.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <PaperClipIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span className="font-medium text-gray-800 dark:text-gray-200 dark:text-gray-200">{mod.titel || 'Dateianhänge'}</span>
                      </div>
                      {anhaenge.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Keine Anhänge</p>
                      ) : (
                      <ul className="space-y-1.5 text-sm">
                        {anhaenge.map((a) => {
                          const downloadUrl = `/api/massenmail/${brief.id}/module/${mod.id}/attachment/${a.id}/download`;
                          return (
                            <li key={a.id} className="flex justify-between items-center text-gray-700 dark:text-gray-300 dark:text-gray-300 group">
                              <button
                                type="button"
                                onClick={() => window.open(downloadUrl, '_blank')}
                                className="flex-1 text-left hover:text-blue-600 hover:underline flex items-center gap-2 min-w-0"
                                title="Datei öffnen/herunterladen"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-blue-600" />
                                <span className="truncate">{a.original_dateiname || a.dateiname}</span>
                              </button>
                              {a.dateigroesse != null && (
                                <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">{formatSize(a.dateigroesse)}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      )}
                    </div>
                  );
                }
                if (mod.modul_typ === 'terminbuchen' && mod.konfiguration?.appointment_config_id) {
                  const config = appointmentConfigs.find((c) => c.id === mod.konfiguration!.appointment_config_id);
                  const bookings = appointmentBookings.filter(
                    (b) => b.configId === mod.konfiguration!.appointment_config_id
                  );
                  return (
                    <div key={mod.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span className="font-medium text-gray-800 dark:text-gray-200 dark:text-gray-200">{mod.titel || config?.title || 'Terminbuchung'}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {config && (
                          <a
                            href={`${window.location.origin}/appointments/${config.public_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Buchungslink öffnen
                          </a>
                        )}
                        <span className="text-gray-600 dark:text-gray-400">
                          {bookings.length} Buchung{bookings.length !== 1 ? 'en' : ''}
                        </span>
                      </div>
                      {bookings.length > 0 && (
                        <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto">
                          {bookings.slice(0, 10).map((b, i) => (
                            <li key={i}>
                              {new Date(b.start_time).toLocaleString('de-DE')} · {b.parent_name || b.student_name || '–'}
                            </li>
                          ))}
                          {bookings.length > 10 && (
                            <li className="text-gray-500 dark:text-gray-400">… und {bookings.length - 10} weitere</li>
                          )}
                        </ul>
                      )}
                    </div>
                  );
                }
                const modLabels: Record<string, string> = {
                  lesebestaetigung: 'Lesebestätigung',
                  texteingabe: 'Textfeld',
                  lernbuerostatistik: 'Lernbürostatistik',
                  umfrage: 'Umfrage',
                  dateiupload: 'Datei-Upload',
                  elternportal_einladung: 'Elternportal-Einladung'
                };
                const label = modLabels[mod.modul_typ] || mod.modul_typ;
                const fragenCount = mod.fragen?.length ?? 0;
                const isUmfrage = mod.modul_typ === 'umfrage';
                const umfrageStat = responseStats.find((r) => r.modul_id === mod.id);
                const umfrageFragenCount = umfrageStat?.umfrage_statistik?.length ?? fragenCount;
                return (
                  <div key={mod.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {mod.modul_typ === 'lesebestaetigung' && <CheckCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                      {mod.modul_typ === 'texteingabe' && <DocumentTextIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                      {mod.modul_typ === 'lernbuerostatistik' && <ChartBarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                      {mod.modul_typ === 'umfrage' && <ChartBarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                      {mod.modul_typ === 'elternportal_einladung' && <UserPlusIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                      <div className="min-w-0">
                        <span className="font-medium text-gray-800 dark:text-gray-200 dark:text-gray-200">{mod.titel || label}</span>
                        {isUmfrage && umfrageFragenCount > 0 && (
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({umfrageFragenCount} Frage{umfrageFragenCount !== 1 ? 'n' : ''})</span>
                        )}
                      </div>
                    </div>
                    {isUmfrage && brief.status === 'versendet' && umfrageModuleStats.some((s) => s.modul_id === mod.id) && (
                      <button
                        type="button"
                        onClick={() => umfrageSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="flex-shrink-0 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        Zu den Details
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Statistik */}
        {brief.status === 'versendet' && stat && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">Rücklauf</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <HoverNamesTooltip
                names={allNames}
                className="cursor-pointer"
                onClick={() => openRecipientDetailModal('Empfänger', recipients)}
              >
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-50">{stat.total}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Empfänger</div>
                </div>
              </HoverNamesTooltip>
              <HoverNamesTooltip
                names={geoeffnetNames}
                className="cursor-pointer"
                onClick={() => openRecipientDetailModal('Link geklickt', recipients.filter((r) => r.antwort_status === 'geoeffnet' || r.antwort_status === 'beantwortet'))}
              >
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors">
                  <div className="text-2xl font-bold text-blue-600">{stat.geoeffnet}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Link geklickt</div>
                </div>
              </HoverNamesTooltip>
              <HoverNamesTooltip
                names={beantwortetNames}
                className="cursor-pointer"
                onClick={() => openRecipientDetailModal('Interagiert', recipients.filter((r) => r.antwort_status === 'beantwortet'))}
              >
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors">
                  <div className="text-2xl font-bold text-green-600">{stat.beantwortet}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Interagiert</div>
                </div>
              </HoverNamesTooltip>
              <HoverNamesTooltip
                names={ausstehendNames}
                className="cursor-pointer"
                onClick={() => openRecipientDetailModal('Ausstehend', recipients.filter((r) => r.antwort_status === 'ausstehend'))}
              >
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors">
                  <div className="text-2xl font-bold text-yellow-600">{stat.ausstehend}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Ausstehend</div>
                </div>
              </HoverNamesTooltip>
              <HoverNamesTooltip
                names={gesperrtNames}
                className="cursor-pointer"
                onClick={() => openRecipientDetailModal('Gesperrt', recipients.filter((r) => r.antwort_status === 'gesperrt'))}
              >
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors">
                  <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stat.gesperrt}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Gesperrt</div>
                </div>
              </HoverNamesTooltip>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{stat.ruecklaufquote}%</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Rücklaufquote</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(stat.ruecklaufquote, 100)}%` }}
                />
              </div>
            </div>
          </section>
        )}

        {/* Umfrage-Resultate */}
        {brief.status === 'versendet' && (umfrageModuleStats.length > 0 || loadingStats) && (
          <section ref={umfrageSectionRef} id="umfrage-ergebnisse">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">Umfrage-Ergebnisse</h3>
            {loadingStats ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                <span className="text-sm">Laden...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {umfrageModuleStats.map((mod) => (
                  <div key={mod.modul_id} className="border border-gray-200 rounded-lg p-4">
                    {mod.modul_titel && (
                      <h4 className="font-medium text-gray-800 dark:text-gray-200 dark:text-gray-200 mb-3">{mod.modul_titel}</h4>
                    )}
                    {(mod.umfrage_statistik || []).map((frage) => (
                      <div key={frage.frage_id} className="mb-4 last:mb-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">{frage.frage_text}</p>
                        {frage.frage_typ === 'freitext' ? (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {(frage.freitext_antworten || []).map((a, i) => (
                              <div key={i} className="text-sm bg-gray-50 p-2 rounded border border-gray-100">{a}</div>
                            ))}
                            {(!frage.freitext_antworten || frage.freitext_antworten.length === 0) && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">Keine Freitext-Antworten</p>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {Object.entries(frage.antworten || {}).map(([optId, data]) => {
                              const names = surveyNamesByOption[frage.frage_id]?.[optId] || [];
                              const recipientsForOption = surveyRecipientsByOption[frage.frage_id]?.[optId] || [];
                              const modalTitle = `${frage.frage_text} – ${data.text || optId}`;
                              return (
                                <HoverNamesTooltip
                                  key={optId}
                                  names={names}
                                  className="block cursor-pointer"
                                  onClick={() => openSurveyDetailModal(modalTitle, recipientsForOption)}
                                >
                                  <div>
                                    <div className="flex justify-between text-sm mb-0.5">
                                      <span className="text-gray-700 dark:text-gray-300 dark:text-gray-300">{data.text || optId}</span>
                                      <span className="font-medium">{data.count} ({data.percentage.toFixed(0)}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-600 h-1.5 rounded-full"
                                        style={{ width: `${data.percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                </HoverNamesTooltip>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {brief.sending && (
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Versand läuft. Sie können weiterarbeiten – Lernrakete meldet sich, wenn er fertig ist. Die Fertig-Meldung bitte mit dem X schließen.
          </p>
        )}
        {brief.status === 'entwurf' && !brief.sending && (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">Noch nicht versendet. Bearbeiten und versenden Sie den Elternbrief im Editor.</p>
        )}
      </div>

      {detailModal && (
        <DetailModal
          isOpen
          onClose={() => setDetailModal(null)}
          title={detailModal.title}
          items={detailModal.items}
        />
      )}
    </div>
  );
};

function getStatusBadge(status: string, sending?: boolean) {
  if (sending) {
    return <Badge variant="warning">Wird versendet</Badge>;
  }
  const variantByStatus: Record<string, 'neutral' | 'success' | 'info'> = {
    entwurf: 'neutral',
    versendet: 'success',
    archiviert: 'info'
  };
  const labels: Record<string, string> = {
    entwurf: 'Entwurf',
    versendet: 'Versendet',
    archiviert: 'Archiviert'
  };
  return <Badge variant={variantByStatus[status] || 'neutral'}>{labels[status] || status}</Badge>;
}

const Massenmail: React.FC<MassenmailProps> = ({ embedded = false, onRefresh }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [elternbriefe, setElternbriefe] = useState<Elternbrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'alle' | 'entwurf' | 'versendet' | 'archiviert' | 'papierkorb'>('alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRecipientOverview, setShowRecipientOverview] = useState(false);
  const [selectedElternbriefId, setSelectedElternbriefId] = useState<number | null>(null);
  const [selectedElternbriefPermission, setSelectedElternbriefPermission] = useState<'read' | 'write' | 'owner'>('owner');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareElternbriefId, setShareElternbriefId] = useState<number | null>(null);
  const [selectedBrief, setSelectedBrief] = useState<Elternbrief | null>(null);

  const filteredElternbriefe = elternbriefe.filter(brief =>
    brief.titel.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brief.inhalt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    loadElternbriefe();
  }, [filter]);

  useEffect(() => {
    const onSendingChanged = () => {
      void loadElternbriefe({ silent: true });
    };
    window.addEventListener(MASSENMAIL_SENDING_EVENT, onSendingChanged);
    return () => window.removeEventListener(MASSENMAIL_SENDING_EVENT, onSendingChanged);
  }, [filter]);

  useEffect(() => {
    const shouldPoll = elternbriefe.some((brief) => brief.sending);
    if (!shouldPoll) return undefined;
    const timer = window.setInterval(() => {
      void loadElternbriefe({ silent: true });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [elternbriefe, filter]);

  // Clear selection when selected brief is no longer in filtered list
  useEffect(() => {
    if (embedded && selectedBrief && !filteredElternbriefe.some((b) => b.id === selectedBrief.id)) {
      setSelectedBrief(null);
    }
  }, [embedded, selectedBrief, filteredElternbriefe]);

  // Mark brief as viewed when user opens it – Badge für neue Rückmeldungen wird zurückgesetzt
  useEffect(() => {
    if (!selectedBrief?.id) return;
    api.post(`/massenmail/${selectedBrief.id}/mark-viewed`).then(() => {
      onRefresh?.();
    }).catch(() => {});
  }, [selectedBrief?.id, onRefresh]);

  const loadElternbriefe = async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      const params: any = {};
      if (filter !== 'alle') {
        params.status = filter;
      }

      const [ownResponse, sharedResponse] = await Promise.all([
        api.get('/massenmail', { params }),
        filter === 'papierkorb'
          ? Promise.resolve({ data: { success: true, data: [] } })
          : api.get('/massenmail/shared', { params })
      ]);

      const localSending = getLocalSendingIds();
      const ownLetters = ownResponse.data.success
        ? ownResponse.data.data.map((brief: any) => ({
            ...brief,
            shared: false,
            sending: Boolean(brief.sending) || localSending.has(Number(brief.id))
          }))
        : [];

      const sharedLetters = sharedResponse.data?.success
        ? sharedResponse.data.data.map((brief: any) => ({
            ...brief,
            shared: true,
            sending: Boolean(brief.sending) || localSending.has(Number(brief.id)),
            shared_permission: brief.shared_permission,
            shared_by: {
              first_name: brief.shared_by_first_name,
              last_name: brief.shared_by_last_name,
              username: brief.shared_by_username
            }
          }))
        : [];

      const combined = [...ownLetters, ...sharedLetters].sort((a: any, b: any) => {
        const dateA = new Date(a.aktualisiert_am || a.erstellt_am).getTime();
        const dateB = new Date(b.aktualisiert_am || b.erstellt_am).getTime();
        return dateB - dateA;
      });

      setElternbriefe(combined);
      setSelectedBrief((prev) => {
        if (!prev?.id) return prev;
        const updated = combined.find((b: Elternbrief) => b.id === prev.id);
        return updated ?? null;
      });
    } catch (error: any) {
      console.error('Error loading Elternbriefe:', error);
      toast.error('Fehler beim Laden der Elternbriefe');
    } finally {
      if (!opts?.silent) {
        setLoading(false);
        onRefresh?.();
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Elternbrief in den Papierkorb verschieben?')) return;
    
    try {
      const response = await api.delete(`/massenmail/${id}`);
      if (response.data.success) {
        toast.success('In den Papierkorb verschoben');
        loadElternbriefe();
      }
    } catch (error: any) {
      console.error('Error moving to trash:', error);
      toast.error('Fehler beim Verschieben in den Papierkorb');
    }
  };

  const handleRestore = async (id: number) => {
    try {
      const response = await api.post(`/massenmail/${id}/restore`);
      if (response.data.success) {
        toast.success('Wiederhergestellt');
        loadElternbriefe();
      }
    } catch (error: any) {
      console.error('Error restoring:', error);
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handlePermanentDelete = async (id: number) => {
    if (!confirm('Elternbrief endgültig löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.')) return;
    try {
      const response = await api.delete(`/massenmail/${id}/permanent`);
      if (response.data.success) {
        toast.success('Endgültig gelöscht');
        loadElternbriefe();
      }
    } catch (error: any) {
      console.error('Error permanently deleting:', error);
      toast.error('Fehler beim endgültigen Löschen');
    }
  };

  const handleShowRecipients = (brief: Elternbrief) => {
    setSelectedElternbriefId(brief.id);
    if (brief.shared) {
      setSelectedElternbriefPermission(brief.shared_permission || 'read');
    } else {
      setSelectedElternbriefPermission('owner');
    }
    setShowRecipientOverview(true);
  };

  const handleRecipientOverviewClose = () => {
    setShowRecipientOverview(false);
    setSelectedElternbriefId(null);
    setSelectedElternbriefPermission('owner');
  };

  const handleRecipientUpdate = () => {
    loadElternbriefe(); // Reload to show updated statistics
  };

  const handleShare = (id: number) => {
    setShareElternbriefId(id);
    setShowShareModal(true);
  };

  const handleDuplicate = async (id: number) => {
    try {
      const response = await api.post(`/massenmail/${id}/duplicate`);
      if (response.data.success) {
        toast.success('Elternbrief dupliziert');
        loadElternbriefe();
      }
    } catch (error: any) {
      console.error('Error duplicating Elternbrief:', error);
      toast.error('Fehler beim Duplizieren');
    }
  };

  const fetchAppointmentBookings = async (modules: ExportModule[]) => {
    const terminModules = modules.filter(
      moduleItem => moduleItem.modul_typ === 'terminbuchen' && moduleItem.konfiguration?.appointment_config_id
    );
    if (terminModules.length === 0) return [];

    const bookingResults = await Promise.all(
      terminModules.map(async moduleItem => {
        const configId = moduleItem.konfiguration?.appointment_config_id;
        try {
          const response = await api.get(`/appointments/configs/${configId}/slots`);
          const slots = response.data?.data || [];
          return slots
            .filter((slot: any) => slot.booking_id && slot.booking_status && slot.booking_status !== 'cancelled')
            .map((slot: any) => ({
              configId,
              moduleTitle: moduleItem.titel || 'Terminbuchung',
              start_time: slot.start_time,
              end_time: slot.end_time,
              parent_name: slot.parent_name,
              parent_email: slot.parent_email,
              student_name: slot.student_name,
              booking_status: slot.booking_status,
              notes: slot.notes
            })) as AppointmentBooking[];
        } catch (error) {
          return [] as AppointmentBooking[];
        }
      })
    );

    return bookingResults.flat();
  };

  const handleExport = async (id: number) => {
    try {
      const [recipientsResponse, briefResponse] = await Promise.all([
        api.get(`/massenmail/${id}/export-results`),
        api.get(`/massenmail/${id}`)
      ]);

      if (!recipientsResponse.data.success || !briefResponse.data.success) {
        toast.error('Export fehlgeschlagen');
        return;
      }

      const exportRecipients = recipientsResponse.data.data || [];
      const exportModules: ExportModule[] = briefResponse.data.data?.module || [];
      const exportBookings = await fetchAppointmentBookings(exportModules);

      const csvData = buildElternbriefResultsCsv(exportRecipients, exportModules, exportBookings);
      downloadCsv(csvData, `elternbrief_${id}_ergebnisse.csv`);
      toast.success('Export erfolgreich');
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Fehler beim Export');
    }
  };

  const filterLabels: Record<typeof filter, string> = {
    alle: 'Alle',
    entwurf: 'Entwurf',
    versendet: 'Versendet',
    archiviert: 'Archiviert',
    papierkorb: 'Papierkorb'
  };

  // Embedded: List+Detail layout like Posteingang (left sidebar, right detail)
  if (embedded) {
    return (
      <>
        {/* Left: Elternbrief list */}
        <div className={`lg:col-span-1 ${selectedBrief ? 'hidden lg:block' : ''}`}>
          <div className="card h-full flex flex-col">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="input-field text-sm"
              >
                {(['alle', 'entwurf', 'versendet', 'archiviert', 'papierkorb'] as const).map((f) => (
                  <option key={f} value={f}>{filterLabels[f]}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field text-sm"
              />
              <button
                onClick={() => navigate('/elternbriefe/neu')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Neuer Elternbrief
              </button>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[calc(100vh-300px)]">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Laden...</p>
                </div>
              ) : filteredElternbriefe.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {searchTerm ? 'Keine Ergebnisse' : 'Keine Elternbriefe'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                  {filteredElternbriefe.map((brief) => (
                    <button
                      key={brief.id}
                      type="button"
                      onClick={() => setSelectedBrief(brief)}
                      className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors border-l-4 ${
                        selectedBrief?.id === brief.id
                          ? 'bg-blue-50 dark:bg-blue-950/40 border-l-blue-600 dark:border-l-blue-400'
                          : 'border-l-transparent'
                      } ${brief.shared ? 'border-indigo-200' : ''}`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`font-medium truncate ${selectedBrief?.id === brief.id ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-gray-50'}`}>
                            {brief.titel || '(Ohne Titel)'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {new Date(brief.versand_datum || brief.erstellt_am).toLocaleDateString('de-DE')}
                          </p>
                          {brief.status === 'versendet' && brief.statistik && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {brief.statistik.beantwortet}/{brief.statistik.total} · {brief.statistik.ruecklaufquote}%
                            </p>
                          )}
                        </div>
                        {getStatusBadge(brief.status, brief.sending)}
                      </div>
                      {brief.shared && (
                        <span className="inline-block mt-1 text-xs text-indigo-600">Geteilt</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Right: Detail view */}
        <div className="lg:col-span-2">
          <ElternbriefDetailView
            brief={selectedBrief}
            onClose={() => setSelectedBrief(null)}
            onShowRecipients={(b) => { setSelectedElternbriefId(b.id); setSelectedElternbriefPermission(b.shared ? (b.shared_permission || 'read') : 'owner'); setShowRecipientOverview(true); }}
            onNavigate={navigate}
            onExport={handleExport}
            onDuplicate={handleDuplicate}
            onShare={handleShare}
            onDelete={handleDelete}
            onRestore={handleRestore}
            onPermanentDelete={handlePermanentDelete}
            filter={filter}
            isShared={(b) => !!b.shared}
            embedded
          />
        </div>
        {selectedElternbriefId && (
          <RecipientOverview
            elternbriefId={selectedElternbriefId}
            isOpen={showRecipientOverview}
            onClose={handleRecipientOverviewClose}
            onUpdate={handleRecipientUpdate}
            canSendReminders={selectedElternbriefPermission !== 'read'}
          />
        )}
        <ElternbriefShareModal
          isOpen={showShareModal}
          elternbriefId={shareElternbriefId}
          currentUserId={user?.id}
          onClose={() => { setShowShareModal(false); setShareElternbriefId(null); }}
          onUpdated={loadElternbriefe}
        />
      </>
    );
  }

  // Non-embedded: original layout
  return (
    <div className="p-6">
      <div className={embedded ? 'mb-4' : 'mb-6'}>
        {!embedded && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 dark:text-gray-200">Massenmailing / Elternbriefe</h1>
              <button
                onClick={() => navigate('/elternbriefe/neu')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                Neuer Elternbrief
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Erstellen und versenden Sie Elternbriefe mit Lesebestätigungen, Umfragen und mehr.
            </p>
          </>
        )}
        {/* Filters and Search */}
        <div className={`flex gap-3 ${embedded ? 'flex-wrap items-center' : 'gap-4 mb-6'}`}>
          {embedded ? (
            <>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                {(['alle', 'entwurf', 'versendet', 'archiviert', 'papierkorb'] as const).map((f) => (
                  <option key={f} value={f}>{filterLabels[f]}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                onClick={() => navigate('/elternbriefe/neu')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Neuer Elternbrief
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                {(['alle', 'entwurf', 'versendet', 'archiviert', 'papierkorb'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      filter === f
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {filterLabels[f]}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredElternbriefe.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <PaperAirplaneIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Keine Elternbriefe gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm
              ? 'Keine Ergebnisse für Ihre Suche'
              : 'Erstellen Sie Ihren ersten Elternbrief'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => navigate('/elternbriefe/neu')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Neuer Elternbrief
            </button>
          )}
        </div>
      )}

      {/* Elternbriefe List */}
      {!loading && filteredElternbriefe.length > 0 && (
        <div className="space-y-4">
          {filteredElternbriefe.map((brief) => (
            <div
              key={brief.id}
              className={`rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${
                brief.shared ? 'bg-indigo-50/40 border-indigo-200 border-l-4' : 'bg-white'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 dark:text-gray-200">
                      {brief.titel}
                    </h3>
                    {brief.shared && (
                      <span className="px-2.5 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700">
                        Geteilt ({brief.shared_permission === 'write' ? 'Schreiben' : 'Lesen'})
                      </span>
                    )}
                    {getStatusBadge(brief.status, brief.sending)}
                  </div>
                  {brief.shared && brief.shared_by && (
                    <div className="text-xs text-indigo-700 mb-2">
                      Freigabe von {brief.shared_by.first_name || ''} {brief.shared_by.last_name || ''} ({brief.shared_by.username})
                    </div>
                  )}
                  <p className="text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                    {brief.inhalt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>
                      Erstellt: {new Date(brief.erstellt_am).toLocaleDateString('de-DE')}
                    </span>
                    {brief.versand_datum && (
                      <span>
                        Versendet: {new Date(brief.versand_datum).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4 flex-shrink-0">
                  {embedded ? (
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === brief.id ? null : brief.id);
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Aktionen"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                      {openMenuId === brief.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenuId(null)}
                            aria-hidden="true"
                          />
                          <div className="absolute right-0 mt-1 w-48 py-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                            <button
                              onClick={() => { navigate(`/elternbriefe/${brief.id}`); setOpenMenuId(null); }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              <EyeIcon className="h-4 w-4" /> Ansehen
                            </button>
                            {brief.status === 'versendet' && (
                              <button
                                onClick={() => { handleShowRecipients(brief); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                <UserGroupIcon className="h-4 w-4" /> Empfänger
                              </button>
                            )}
                            {!brief.shared && brief.status === 'entwurf' && !brief.sending && filter !== 'papierkorb' && (
                              <button
                                onClick={() => { navigate(`/elternbriefe/${brief.id}/bearbeiten`); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                <PencilIcon className="h-4 w-4" /> Bearbeiten
                              </button>
                            )}
                            {!brief.shared && (
                              <button
                                onClick={() => { handleDuplicate(brief.id); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                <DocumentDuplicateIcon className="h-4 w-4" /> Duplizieren
                              </button>
                            )}
                            {brief.status === 'versendet' && (
                              <button
                                onClick={() => { handleExport(brief.id); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" /> Exportieren
                              </button>
                            )}
                            {!brief.shared && filter !== 'papierkorb' && (
                              <button
                                onClick={() => { handleShare(brief.id); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                              >
                                <UserPlusIcon className="h-4 w-4" /> Teilen
                              </button>
                            )}
                            {!brief.shared && filter !== 'papierkorb' && (
                              <button
                                onClick={() => { handleDelete(brief.id); setOpenMenuId(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <TrashIcon className="h-4 w-4" /> In Papierkorb
                              </button>
                            )}
                            {!brief.shared && filter === 'papierkorb' && (
                              <>
                                <button
                                  onClick={() => { handleRestore(brief.id); setOpenMenuId(null); }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 flex items-center gap-2"
                                >
                                  <ArrowUturnLeftIcon className="h-4 w-4" /> Wiederherstellen
                                </button>
                                <button
                                  onClick={() => { handlePermanentDelete(brief.id); setOpenMenuId(null); }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <TrashIcon className="h-4 w-4" /> Endgültig löschen
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate(`/elternbriefe/${brief.id}`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ansehen"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {brief.status === 'versendet' && (
                        <button
                          onClick={() => handleShowRecipients(brief)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Empfänger verwalten"
                        >
                          <UserGroupIcon className="h-5 w-5" />
                        </button>
                      )}
                      {!brief.shared && brief.status === 'entwurf' && !brief.sending && filter !== 'papierkorb' && (
                        <button
                          onClick={() => navigate(`/elternbriefe/${brief.id}/bearbeiten`)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          title="Bearbeiten"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      )}
                      {!brief.shared && (
                        <button
                          onClick={() => handleDuplicate(brief.id)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          title="Duplizieren"
                        >
                          <DocumentDuplicateIcon className="h-5 w-5" />
                        </button>
                      )}
                      {brief.status === 'versendet' && (
                        <button
                          onClick={() => handleExport(brief.id)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          title="Exportieren"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      )}
                      {!brief.shared && filter !== 'papierkorb' && (
                        <button
                          onClick={() => handleDelete(brief.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="In den Papierkorb"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                      {!brief.shared && filter === 'papierkorb' && (
                        <>
                          <button
                            onClick={() => handleRestore(brief.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Wiederherstellen"
                          >
                            <ArrowUturnLeftIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(brief.id)}
                            className="p-2 text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Endgültig löschen"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {!brief.shared && filter !== 'papierkorb' && (
                        <button
                          onClick={() => handleShare(brief.id)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Teilen"
                        >
                          <UserPlusIcon className="h-5 w-5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Statistics */}
              {brief.status === 'versendet' && brief.statistik && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-800 dark:text-gray-200 dark:text-gray-200">
                        {brief.statistik.total}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Empfänger</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {brief.statistik.geoeffnet}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Geöffnet</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {brief.statistik.beantwortet}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Beantwortet</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {brief.statistik.ausstehend}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Ausstehend</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                        {brief.statistik.gesperrt}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Gesperrt</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {brief.statistik.ruecklaufquote}%
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Rücklauf</div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ChartBarIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Rücklaufquote</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-green-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${brief.statistik.ruecklaufquote}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recipient Overview Modal */}
      {selectedElternbriefId && (
        <RecipientOverview
          elternbriefId={selectedElternbriefId}
          isOpen={showRecipientOverview}
          onClose={handleRecipientOverviewClose}
          onUpdate={handleRecipientUpdate}
          canSendReminders={selectedElternbriefPermission !== 'read'}
        />
      )}

      <ElternbriefShareModal
        isOpen={showShareModal}
        elternbriefId={shareElternbriefId}
        currentUserId={user?.id}
        onClose={() => {
          setShowShareModal(false);
          setShareElternbriefId(null);
        }}
        onUpdated={loadElternbriefe}
      />
    </div>
  );
};

export default Massenmail;
